import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../utils/api';

const DEFAULT_CATEGORY = 'Generale';

const NewProductFromInvoiceDialog = ({ open, onClose, onSave, initialCode = '', initialName = '', initialPurchasePrice = 0, initialSellingPrice, initialImage }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([DEFAULT_CATEGORY]);
  const [laborCost, setLaborCost] = useState(30);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: DEFAULT_CATEGORY,
    brand: '',
    purchasePrice: 0,
    sellingPrice: 0,
    assemblyTime: 15,
    totalPriceWithLabor: 0,
    minStock: 1,
    stock: 0,
    image: ''
  });
  const [b2bAnchorEl, setB2bAnchorEl] = useState(null);
  const [b2bResults, setB2bResults] = useState([]);
  const [b2bLoading, setB2bLoading] = useState(false);
  const [b2bError, setB2bError] = useState(null);
  const [b2bSearchInput, setB2bSearchInput] = useState('');
  const [priceConflictDialogOpen, setPriceConflictDialogOpen] = useState(false);
  const [priceConflictProduct, setPriceConflictProduct] = useState(null);
  const [priceConflictLoading, setPriceConflictLoading] = useState(false);
  const [priceConflictSellingStep, setPriceConflictSellingStep] = useState(null);
  const [editableSellingPriceInDialog, setEditableSellingPriceInDialog] = useState('');

  const searchB2b = async (term) => {
    const t = (term || '').trim();
    if (!t) return;
    setB2bLoading(true);
    setB2bError(null);
    setB2bResults([]);
    try {
      const res = await api.get(`/b2b-products/code/${encodeURIComponent(t)}`);
      if (res.data) {
        setB2bResults([res.data]);
      }
    } catch (ex) {
      if (ex.response?.status === 404) {
        const listRes = await api.get('/b2b-products', {
          params: { search: t, limit: 20, page: 1 }
        });
        const products = listRes.data?.products || listRes.data || [];
        setB2bResults(Array.isArray(products) ? products : []);
        if (!Array.isArray(products) || products.length === 0) {
          setB2bError('Nessun articolo trovato nel listino B2B');
        }
      } else {
        setB2bError('Errore ricerca listino B2B');
      }
    } finally {
      setB2bLoading(false);
    }
  };

  const handleOpenB2bSearch = (e) => {
    setB2bAnchorEl(e.currentTarget);
    setB2bResults([]);
    setB2bError(null);
    const code = formData.code?.trim() || '';
    setB2bSearchInput(code);
    if (code) setTimeout(() => searchB2b(code), 150);
  };

  const handleCloseB2bPopover = () => {
    setB2bAnchorEl(null);
    setB2bResults([]);
    setB2bError(null);
  };

  const handleImportFromB2b = (b2b) => {
    const category = (b2b.category || b2b.department || DEFAULT_CATEGORY).trim();
    const catExists = categories.some((c) => String(c).toLowerCase() === String(category).toLowerCase());
    setFormData((prev) => ({
      ...prev,
      code: b2b.code || prev.code,
      name: (b2b.name || b2b.code || prev.name).toString().toUpperCase(),
      description: (b2b.name || b2b.description || prev.description || '').toString().toUpperCase(),
      category: catExists ? (categories.find((c) => String(c).toLowerCase() === String(category).toLowerCase()) || prev.category) : prev.category,
      brand: (b2b.brand || prev.brand || '').toString().toUpperCase(),
      purchasePrice: prev.purchasePrice,
      sellingPrice: parseFloat(b2b.sellingPrice) || prev.sellingPrice,
      minStock: b2b.minQty != null ? Number(b2b.minQty) : prev.minStock,
      image: (b2b.image || prev.image || '').toString().trim()
    }));
    handleCloseB2bPopover();
  };

  useEffect(() => {
    if (open) {
      const selling = initialSellingPrice != null && initialSellingPrice !== '' ? parseFloat(initialSellingPrice) : (initialPurchasePrice ? initialPurchasePrice * 1.22 : 0);
      const defaultLaborPerMin = (30 / 60) * 15; // 15 min, 30 €/h default
      setFormData({
        code: initialCode,
        name: initialName || initialCode,
        description: initialName || initialCode,
        category: DEFAULT_CATEGORY,
        brand: '',
        purchasePrice: initialPurchasePrice,
        sellingPrice: selling,
        assemblyTime: 15,
        totalPriceWithLabor: (selling + defaultLaborPerMin).toFixed(2),
        minStock: 1,
        stock: 0,
        image: (initialImage && String(initialImage).trim()) || ''
      });
      setError(null);
      setB2bAnchorEl(null);
      setB2bResults([]);
      setB2bError(null);
    }
  }, [open, initialCode, initialName, initialPurchasePrice, initialSellingPrice, initialImage]);

  // Se mancano prezzo di vendita o foto (es. riga non da "articoli da ricevere"), precompila dal listino B2B
  useEffect(() => {
    if (!open || !(initialCode && String(initialCode).trim())) return;
    const hasSelling = initialSellingPrice != null && initialSellingPrice !== '' && Number(initialSellingPrice) > 0;
    const hasImage = initialImage != null && String(initialImage).trim() !== '';
    if (hasSelling && hasImage) return;
    let cancelled = false;
    const fetchB2bForPrefill = async () => {
      try {
        const res = await api.get(`/b2b-products/code/${encodeURIComponent(initialCode.trim())}`);
        if (cancelled || !res.data) return;
        const b2b = res.data;
        setFormData((prev) => ({
          ...prev,
          ...(!hasSelling && { sellingPrice: parseFloat(b2b.sellingPrice) || prev.sellingPrice }),
          ...(!hasImage && { image: (b2b.image && String(b2b.image).trim()) || prev.image })
        }));
      } catch (e) {
        if (e.response?.status === 404) {
          try {
            const listRes = await api.get('/b2b-products', { params: { search: initialCode.trim(), limit: 5, page: 1 } });
            const products = listRes.data?.products || listRes.data || [];
            if (!cancelled && Array.isArray(products) && products.length > 0) {
              const b2b = products[0];
              setFormData((prev) => ({
                ...prev,
                ...(!hasSelling && { sellingPrice: parseFloat(b2b.sellingPrice) || prev.sellingPrice }),
                ...(!hasImage && { image: (b2b.image && String(b2b.image).trim()) || prev.image })
              }));
            }
          } catch (_) {}
        }
      }
    };
    fetchB2bForPrefill();
    return () => { cancelled = true; };
  }, [open, initialCode, initialSellingPrice, initialImage]);

  useEffect(() => {
    if (open) {
      loadCategories();
      loadLaborCost();
    }
  }, [open]);

  const loadCategories = async () => {
    try {
      const response = await api.get('/products/categories/all');
      if (response.data && Array.isArray(response.data)) {
        const sorted = response.data
          .filter((c) => c !== 'Generale')
          .sort((a, b) => String(a).localeCompare(String(b), 'it', { sensitivity: 'base' }));
        setCategories([DEFAULT_CATEGORY, ...sorted]);
      }
    } catch (err) {
      console.error('Errore caricamento categorie:', err);
      setCategories([DEFAULT_CATEGORY]);
    }
  };

  const loadLaborCost = async () => {
    try {
      const companyId = localStorage.getItem('companyId');
      if (companyId) {
        const response = await api.get('/company-settings', { headers: { 'x-company-id': companyId } });
        if (response.data?.laborHourlyRate) setLaborCost(parseFloat(response.data.laborHourlyRate));
      }
    } catch (err) {
      console.error('Errore costo manodopera:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssemblyTimeChange = (e) => {
    const assemblyTime = parseFloat(e.target.value) || 0;
    const sellingPrice = parseFloat(formData.sellingPrice) || 0;
    const laborPrice = (laborCost / 60) * assemblyTime;
    setFormData((prev) => ({
      ...prev,
      assemblyTime: e.target.value,
      totalPriceWithLabor: (sellingPrice + laborPrice).toFixed(2)
    }));
  };

  const handleSellingPriceChange = (e) => {
    const sellingPrice = parseFloat(e.target.value) || 0;
    const assemblyTime = parseFloat(formData.assemblyTime) || 0;
    const laborPrice = (laborCost / 60) * assemblyTime;
    setFormData((prev) => ({
      ...prev,
      sellingPrice: e.target.value,
      totalPriceWithLabor: (sellingPrice + laborPrice).toFixed(2)
    }));
  };

  const handleTotalPriceChange = (e) => {
    const total = parseFloat(e.target.value) || 0;
    const sellingPrice = parseFloat(formData.sellingPrice) || 0;
    const laborPrice = Math.max(0, total - sellingPrice);
    const newAssemblyTime = laborCost > 0 ? Math.round((laborPrice * 60) / laborCost * 10) / 10 : 0;
    setFormData((prev) => ({
      ...prev,
      totalPriceWithLabor: e.target.value,
      assemblyTime: newAssemblyTime >= 0 ? newAssemblyTime : prev.assemblyTime
    }));
  };

  const qtyToAddForLoad = () => parseInt(formData.stock, 10) || 0;

  const doLoadFromInvoiceAndClose = async (product) => {
    const qtyToAdd = qtyToAddForLoad();
    if (qtyToAdd > 0) {
      await api.post('/warehouse-movements/load', {
        productId: product.id,
        quantity: qtyToAdd,
        purchasePrice: parseFloat(formData.purchasePrice) || 0,
        notes: 'Carico da fattura (articolo già in magazzino)',
        reason: 'Carico da fattura'
      });
    }
    onSave(product);
    setPriceConflictDialogOpen(false);
    setPriceConflictProduct(null);
    onClose();
  };

  const openSellingStep = (action) => {
    if (!priceConflictProduct) return;
    const oldPurchase = parseFloat(priceConflictProduct.purchasePrice) || 0;
    const oldSelling = parseFloat(priceConflictProduct.sellingPrice) || 0;
    const newPurchase = parseFloat(formData.purchasePrice) || 0;
    const qty = qtyToAddForLoad();
    const stock = parseInt(priceConflictProduct.stock, 10) || 0;
    let proposedSelling = oldSelling;
    if (action === 'average' && oldPurchase > 0 && (stock + qty) > 0) {
      const weightedPurchase = (stock * oldPurchase + qty * newPurchase) / (stock + qty);
      const markup = oldSelling / oldPurchase;
      proposedSelling = Math.round(weightedPurchase * markup * 100) / 100;
    } else if (action === 'force') {
      proposedSelling = parseFloat(formData.sellingPrice) || (oldPurchase > 0 ? Math.round(newPurchase * (oldSelling / oldPurchase) * 100) / 100 : newPurchase * 1.22);
    }
    setPriceConflictSellingStep({ action, oldSellingPrice: oldSelling, proposedSellingPrice: proposedSelling });
    setEditableSellingPriceInDialog(proposedSelling.toFixed(2));
  };

  const handlePriceConflictOption = async (option, updateSelling, customSellingPrice) => {
    if (!priceConflictProduct) return;
    setPriceConflictLoading(true);
    setError(null);
    try {
      const productId = priceConflictProduct.id;
      const newPurchasePrice = parseFloat(formData.purchasePrice) || 0;
      const quantityLoaded = qtyToAddForLoad();
      const oldSelling = parseFloat(priceConflictProduct.sellingPrice) || 0;
      let productToSave = priceConflictProduct;
      const proposed = priceConflictSellingStep?.proposedSellingPrice;
      const effectiveSelling = (updateSelling && Number.isFinite(parseFloat(customSellingPrice)))
        ? parseFloat(customSellingPrice)
        : (updateSelling ? (proposed ?? (parseFloat(formData.sellingPrice) || newPurchasePrice * 1.22)) : oldSelling);
      if (option === 'average') {
        await api.post('/products/update-prices-on-load', {
          productId,
          newPurchasePrice,
          quantityLoaded,
          action: 'average'
        });
        const res = await api.get(`/products/${productId}`);
        productToSave = res.data;
        if (!updateSelling) {
          await api.put(`/products/${productId}`, { ...productToSave, sellingPrice: oldSelling });
          const refetch = await api.get(`/products/${productId}`);
          productToSave = refetch.data;
        } else if (effectiveSelling !== parseFloat(productToSave.sellingPrice)) {
          await api.put(`/products/${productId}`, { ...productToSave, sellingPrice: effectiveSelling });
          const refetch = await api.get(`/products/${productId}`);
          productToSave = refetch.data;
        }
      } else if (option === 'force') {
        const newSelling = updateSelling ? (Number.isFinite(effectiveSelling) ? effectiveSelling : newPurchasePrice * 1.22) : oldSelling;
        await api.put(`/products/${productId}`, {
          ...priceConflictProduct,
          purchasePrice: newPurchasePrice,
          sellingPrice: newSelling
        });
        const res = await api.get(`/products/${productId}`);
        productToSave = res.data;
      }
      setPriceConflictSellingStep(null);
      setEditableSellingPriceInDialog('');
      await doLoadFromInvoiceAndClose(productToSave);
    } catch (err) {
      const errData = err.response?.data;
      setError(errData?.msg || errData?.error || err.message || 'Errore durante l\'aggiornamento prezzi');
    } finally {
      setPriceConflictLoading(false);
    }
  };

  const handlePriceConflictLeaveUnchanged = async () => {
    if (!priceConflictProduct) return;
    setPriceConflictLoading(true);
    setError(null);
    try {
      await doLoadFromInvoiceAndClose(priceConflictProduct);
    } catch (err) {
      const errData = err.response?.data;
      setError(errData?.msg || errData?.error || err.message || 'Errore durante il carico');
    } finally {
      setPriceConflictLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.code.trim() || !formData.name.trim()) {
      setError('Codice e nome obbligatori');
      return;
    }
    if (!formData.category?.trim()) {
      setError('Categoria obbligatoria');
      return;
    }
    setSaving(true);
    setError(null);
    const code = formData.code.trim();
    const qtyToAdd = parseInt(formData.stock, 10) || 0;
    try {
      let existingProduct = null;
      try {
        const getRes = await api.get(`/products/code/${encodeURIComponent(code)}`);
        existingProduct = getRes.data;
      } catch (_) {
        existingProduct = null;
      }
      if (existingProduct && existingProduct.id) {
        const invoicePrice = parseFloat(formData.purchasePrice) || 0;
        const warehousePrice = parseFloat(existingProduct.purchasePrice) || 0;
        if (qtyToAdd > 0 && Math.abs(invoicePrice - warehousePrice) > 0.01) {
          setPriceConflictProduct(existingProduct);
          setPriceConflictDialogOpen(true);
          setSaving(false);
          return;
        }
        await doLoadFromInvoiceAndClose(existingProduct);
        return;
      }
      const desc = (formData.description || formData.name || formData.code || 'Prodotto').trim().toUpperCase() || 'Prodotto';
      const payload = {
        code,
        name: formData.name.trim().toUpperCase(),
        description: desc,
        category: (formData.category || DEFAULT_CATEGORY).trim().toUpperCase(),
        brand: (formData.brand || '').trim().toUpperCase(),
        purchasePrice: parseFloat(formData.purchasePrice) || 0,
        price: parseFloat(formData.sellingPrice) || parseFloat(formData.purchasePrice) || 0,
        stock: qtyToAdd,
        minStock: parseInt(formData.minStock, 10) || 1,
        assemblyTime: parseFloat(formData.assemblyTime) || 0,
        isActive: true,
        image: (formData.image || '').trim() || undefined
      };
      const res = await api.post('/products', payload);
      onSave(res.data);
      onClose();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.msg || (Array.isArray(errData?.errors) && errData.errors[0]?.msg) || errData?.message || 'Errore creazione prodotto';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nuovo articolo in magazzino</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="code"
                label="Codice articolo *"
                fullWidth
                value={formData.code}
                onChange={handleChange}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Cerca nel listino B2B e importa i dati">
                        <IconButton
                          size="small"
                          onClick={handleOpenB2bSearch}
                          aria-label="Cerca nel listino B2B"
                        >
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
              <Popover
                open={Boolean(b2bAnchorEl)}
                anchorEl={b2bAnchorEl}
                onClose={handleCloseB2bPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { minWidth: 420, maxWidth: 560, maxHeight: 480, bgcolor: '#fff' } }}
              >
                <Box sx={{ p: 2, color: '#212121' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(0,0,0,0.6)' }}>
                    Cerca nel listino Shop B2B
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Cerca per codice o descrizione..."
                      value={b2bSearchInput}
                      onChange={(e) => setB2bSearchInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchB2b(b2bSearchInput))}
                      fullWidth
                    />
                    <Button variant="outlined" onClick={() => searchB2b(b2bSearchInput)} disabled={b2bLoading || !b2bSearchInput.trim()}>
                      {b2bLoading ? <CircularProgress size={24} /> : 'Cerca'}
                    </Button>
                  </Box>
                  {b2bError && (
                    <Alert severity="info" sx={{ mb: 1 }} onClose={() => setB2bError(null)}>
                      {b2bError}
                    </Alert>
                  )}
                  {b2bResults.length > 0 && (
                    <List dense sx={{ maxHeight: 340, overflow: 'auto' }}>
                      {b2bResults.map((p) => (
                        <ListItem
                          key={p.id || p.code}
                          secondaryAction={
                            <Button size="small" variant="contained" onClick={() => handleImportFromB2b(p)}>
                              Importa
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={p.name || p.code}
                            secondary={`${p.code} · € ${parseFloat(p.sellingPrice || 0).toFixed(2)} (vendita)`}
                            primaryTypographyProps={{ fontWeight: 500 }}
                            sx={{ '& .MuiListItemText-primary': { color: '#212121' }, '& .MuiListItemText-secondary': { color: 'rgba(0,0,0,0.6)' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Popover>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Nome prodotto *"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value.toUpperCase() }))}
                required
                inputProps={{ maxLength: 80 }}
                helperText={`${(formData.name || '').length}/80 caratteri`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Descrizione"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value.toUpperCase() }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Categoria</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Categoria"
                  error={!formData.category}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="brand"
                label="Marca"
                fullWidth
                value={formData.brand}
                onChange={(e) => setFormData((prev) => ({ ...prev, brand: e.target.value.toUpperCase() }))}
              />
            </Grid>
            {formData.image && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Immagine (importata da listino B2B)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <Box
                    component="img"
                    src={formData.image}
                    alt="Anteprima"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    sx={{ width: 56, height: 56, objectFit: 'contain', border: 1, borderColor: 'divider', borderRadius: 1 }}
                  />
                  <Button size="small" onClick={() => setFormData((prev) => ({ ...prev, image: '' }))}>
                    Rimuovi
                  </Button>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                name="purchasePrice"
                label="Prezzo di acquisto IVA esclusa"
                type="number"
                fullWidth
                value={formData.purchasePrice}
                onChange={handleChange}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Prezzo di acquisto IVA inclusa (22%)"
                type="number"
                fullWidth
                value={((parseFloat(formData.purchasePrice) || 0) * 1.22).toFixed(2)}
                disabled
                inputProps={{ step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="sellingPrice"
                label="Prezzo di vendita"
                type="number"
                fullWidth
                value={formData.sellingPrice}
                onChange={handleSellingPriceChange}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="assemblyTime"
                label="Tempo di montaggio (minuti)"
                type="number"
                fullWidth
                value={formData.assemblyTime}
                onChange={handleAssemblyTimeChange}
                required
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="totalPriceWithLabor"
                label="Prezzo montaggio incluso"
                type="number"
                fullWidth
                value={formData.totalPriceWithLabor}
                onChange={handleTotalPriceChange}
                inputProps={{ step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="minStock"
                label="Scorta minima"
                type="number"
                fullWidth
                value={formData.minStock}
                onChange={handleChange}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="stock"
                label="Giacenza iniziale"
                type="number"
                fullWidth
                value={formData.stock}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            saving ||
            !formData.code?.trim() ||
            !formData.name?.trim() ||
            !formData.category?.trim() ||
            !formData.sellingPrice ||
            !formData.minStock ||
            formData.assemblyTime === '' ||
            formData.assemblyTime == null
          }
        >
          {saving ? 'Salvataggio...' : 'Aggiungi al magazzino'}
        </Button>
      </DialogActions>

      <Dialog open={priceConflictDialogOpen} onClose={() => { if (!priceConflictLoading) { setPriceConflictDialogOpen(false); setPriceConflictSellingStep(null); setEditableSellingPriceInDialog(''); } }} maxWidth="sm" fullWidth>
        <DialogTitle>{priceConflictSellingStep ? 'Modifica prezzo di vendita' : 'Variazione prezzo di acquisto'}</DialogTitle>
        <DialogContent>
          {error && priceConflictDialogOpen && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {priceConflictSellingStep ? (
            <Box sx={{ pt: 1 }}>
              <Typography gutterBottom>
                Vuoi aggiornare anche il prezzo di vendita?
              </Typography>
              <Typography sx={{ mb: 1 }}>
                Prezzo di vendita attuale: <b>€{priceConflictSellingStep.oldSellingPrice.toFixed(2)}</b>
              </Typography>
              <TextField
                fullWidth
                label="Nuovo prezzo di vendita"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={editableSellingPriceInDialog}
                onChange={(e) => setEditableSellingPriceInDialog(e.target.value)}
                sx={{ mb: 2 }}
                disabled={priceConflictLoading}
              />
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => handlePriceConflictOption(priceConflictSellingStep.action, true, editableSellingPriceInDialog)}
                variant="contained"
                color="primary"
                disabled={priceConflictLoading}
              >
                {priceConflictLoading ? <CircularProgress size={20} /> : 'Sì, aggiorna anche il prezzo di vendita'}
              </Button>
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => handlePriceConflictOption(priceConflictSellingStep.action, false)}
                variant="outlined"
                disabled={priceConflictLoading}
              >
                No, lascia invariato il prezzo di vendita
              </Button>
            </Box>
          ) : priceConflictProduct && (
            <Box sx={{ pt: 1 }}>
              <Typography gutterBottom>
                Il prezzo della fattura (<b>€{(parseFloat(formData.purchasePrice) || 0).toFixed(2)}</b>) è diverso da quello attualmente in magazzino (<b>€{(parseFloat(priceConflictProduct.purchasePrice) || 0).toFixed(2)}</b>).
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Vuoi aggiornare il prezzo di acquisto (e eventualmente il prezzo di vendita)?
              </Typography>
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => openSellingStep('average')}
                variant="contained"
                color="primary"
                disabled={priceConflictLoading}
              >
                Sì, aggiorna con la media ponderata
              </Button>
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => openSellingStep('force')}
                variant="contained"
                disabled={priceConflictLoading}
              >
                Sì, usa il prezzo della fattura
              </Button>
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={handlePriceConflictLeaveUnchanged}
                variant="outlined"
                color="secondary"
                disabled={priceConflictLoading}
              >
                No, lascia i prezzi invariati e registra solo il carico
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {priceConflictSellingStep ? (
            <Button onClick={() => setPriceConflictSellingStep(null)} disabled={priceConflictLoading}>
              Indietro
            </Button>
          ) : null}
          <Button onClick={() => { setPriceConflictDialogOpen(false); setPriceConflictSellingStep(null); setEditableSellingPriceInDialog(''); }} disabled={priceConflictLoading}>
            Annulla
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default NewProductFromInvoiceDialog;
